import React, { useState, useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";


import ZenDemoFooter from "./ZenDemoFooter"

import {
  FaCode,
  FaSave,
  FaTrash,

  FaMagic,
  FaCog,
  FaTerminal,
} from "react-icons/fa";

import MonoButton from "../kits/DesignKit/MonoButton";
import PaperDarkBG from "../kits/DesignKit/PaperDarkBG";
import PaperBG from "../kits/DesignKit/PaperBG";
import BookingModal from "./BookingModal";
import MarkdownToolbar from "./MarkdownToolbar";
import PreviewToolbar from "./PreviewToolbar";
import ZenEditorAIPanel from "./ZenEditorAIPanel";
import ZenEditorCodePanel from "./ZenEditorCodePanel";
import ZenEditorSettings from "./ZenEditorSettings";
import ZenEditorExportModal from "./ZenEditorExportModal";
import CustomCourseRequestPr from "./CustomCourseRequestPr";
import { useEditorSettings } from "../hooks/useEditorSettings";
import EditorJS from "@editorjs/editorjs";
import Header from "@editorjs/header";
import Paragraph from "@editorjs/paragraph";
import List from "@editorjs/list";
import Quote from "@editorjs/quote";
import Delimiter from "@editorjs/delimiter";
import ZenPostStudioLandingPageDemo from "./ZenPostStudioLandingPageDemo";

// Demo-Beispielinhalt
const DEMO_MARKDOWN = `# Willkommen beim ZenEditor Demo

Dies ist eine **interaktive Demo** des ZenEditors. Du kannst hier frei experimentieren!

## Features

- ✅ Markdown-Support
- ✅ Live-Preview
- ✅ LocalStorage (deine Änderungen bleiben erhalten)
- ✅ Export-Funktion

## Code-Beispiel

\`\`\`javascript
function greet(name) {
  return \`Hello, \${name}!\`;
}

console.log(greet("World"));
\`\`\`

## Zitat

> "Die beste Lösung ist oft die einfachste."
> — Denis Bitter

---

**Probier es aus!** Bearbeite diesen Text im Editor links und sieh die Änderungen live.

### Was du tun kannst:

1. Text bearbeiten
2. Markdown-Syntax verwenden
3. Speichern (LocalStorage)
4. Als .md Datei exportieren

---

*Erstellt mit dem ZenEditor - hybrid, flexibel, portabel.*`;

const WORKFLOW_STEPS = [
  {
    step: "01",
    title: "Schreiben oder Import",
    description: "Nutze ZenEditor oder importiere bestehende Blogposts.",
  },
  {
    step: "02",
    title: "Plattformen auswahlen",
    description: "Wahle die Kanale fur deine Inhalte in Sekunden.",
  },
  {
    step: "03",
    title: "Posts generieren",
    description: "KI erstellt Plattform-optimierte Varianten automatisch.",
  },
  {
    step: "04",
    title: "Feinschliff & teilen",
    description: "Passe Details an, kopiere oder publiziere direkt.",
  },
];

// Markdown zu EditorJS Blocks Konverter
function mdToEditorBlocks(md) {
  const lines = md.split("\n");
  const blocks = [];
  let listBuffer = [];

  const flushList = () => {
    if (listBuffer.length > 0) {
      blocks.push({
        type: "list",
        data: { style: "unordered", items: [...listBuffer] },
      });
      listBuffer = [];
    }
  };

  let paragraphBuffer = [];

  const flushParagraph = () => {
    if (paragraphBuffer.length > 0) {
      const text = paragraphBuffer.join("\n").trim();
      if (text) {
        blocks.push({
          type: "paragraph",
          data: { text },
        });
      }
      paragraphBuffer = [];
    }
  };

  let i = 0;
  while (i < lines.length) {
    const line = lines[i].replace(/\r$/, "");

    // Header
    if (/^#{1,6}\s+/.test(line)) {
      flushList();
      flushParagraph();
      const level = Math.min(6, line.match(/^#{1,6}/)[0].length);
      const text = line.replace(/^#{1,6}\s+/, "");
      const headerLevel = level === 1 ? 2 : Math.min(4, Math.max(2, level));
      blocks.push({
        type: "header",
        data: { level: headerLevel, text },
      });
      i++;
      continue;
    }

    // Quote
    if (/^>\s+/.test(line)) {
      flushList();
      flushParagraph();
      blocks.push({
        type: "quote",
        data: { text: line.replace(/^>\s+/, "") },
      });
      i++;
      continue;
    }

    // List
    if (/^-\s+/.test(line) || /^\*\s+/.test(line) || /^\d+\.\s+/.test(line)) {
      const item = line.replace(/^(-|\*|\d+\.)\s+/, "");
      listBuffer.push(item);
      i++;
      continue;
    }

    // Code Block
    const codeFenceMatch = line.match(/^```([\w-]*)/);
    if (codeFenceMatch) {
      flushList();
      flushParagraph();

      const codeLines = [];
      i++; // Skip opening fence

      while (i < lines.length && !lines[i].match(/^```/)) {
        codeLines.push(lines[i]);
        i++;
      }

      if (i < lines.length && lines[i].match(/^```/)) {
        i++;
      }

      blocks.push({
        type: "paragraph",
        data: {
          text: `<code>${codeLines.join("\n")}</code>`,
        },
      });
      continue;
    }

    // Delimiter (---, ***, ___)
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(line.trim())) {
      flushList();
      flushParagraph();
      blocks.push({
        type: "delimiter",
        data: {},
      });
      i++;
      continue;
    }

    // Blank line
    if (line.trim() === "") {
      flushList();
      flushParagraph();
      i++;
      continue;
    }

    // Default: paragraph
    paragraphBuffer.push(line);
    i++;
  }

  flushList();
  flushParagraph();

  return blocks;
}

// EditorJS Blocks zu Markdown Konverter
function editorBlocksToMarkdown(blocks) {
  const mdArr = [];

  blocks.forEach(({ type, data }) => {
    switch (type) {
      case "header":
        const headerLevel = data.level || 2;
        const headerPrefix = "#".repeat(headerLevel);
        mdArr.push(`${headerPrefix} ${data.text || ""}`);
        break;
      case "paragraph":
        mdArr.push(data.text || "");
        break;
      case "quote":
        mdArr.push(`> ${data.text || ""}`);
        break;
      case "list":
        const items = data.items || [];
        items.forEach((item) => {
          const itemText = typeof item === "string" ? item : item.content || "";
          mdArr.push(`- ${itemText}`);
        });
        break;
      case "delimiter":
        mdArr.push("---");
        break;
      default:
        break;
    }
  });

  return mdArr.join("\n\n");
}

function ZenEditorDemo() {
  const { settings } = useEditorSettings();
  const [markdown, setMarkdown] = useState("");
  const [showPreview, setShowPreview] = useState(true);
  const [showBooking, setShowBooking] = useState(false);
  const [isMarkdownMode, setIsMarkdownMode] = useState(true);
  const [activeWorkflowStep, setActiveWorkflowStep] = useState(0);
  const [, setEditorData] = useState(null);
  const [isNarrow, setIsNarrow] = useState(false);
  const [selection, setSelection] = useState({ start: 0, end: 0 });
  const [miniToolbar, setMiniToolbar] = useState({ visible: false, top: 0, left: 0 });
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [showCodePanel, setShowCodePanel] = useState(false);
  const [selectedText, setSelectedText] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const ejInstance = useRef(null);
  const textareaRef = useRef(null);



  // Lade aus LocalStorage beim Mount
  useEffect(() => {
    const saved = localStorage.getItem("zeneditor_demo_content");
    if (saved) {
      setMarkdown(saved);
    } else {
      setMarkdown(DEMO_MARKDOWN);
    }
  }, []);

  // Auto-Save functionality
  useEffect(() => {
    if (!settings.autoSave) return;

    const autoSaveTimer = setInterval(async () => {
      try {
        let finalMarkdown = markdown;

        if (!isMarkdownMode) {
          // Im EditorJS-Modus: Daten zu Markdown konvertieren
          if (ejInstance.current) {
            const output = await ejInstance.current.save();
            finalMarkdown = editorBlocksToMarkdown(output.blocks);
          }
        }

        localStorage.setItem("zeneditor_demo_content", finalMarkdown);
        console.log("✅ Auto-saved at", new Date().toLocaleTimeString());
      } catch (err) {
        console.error("Auto-save failed:", err);
      }
    }, settings.autoSaveInterval * 1000);

    return () => clearInterval(autoSaveTimer);
  }, [settings.autoSave, settings.autoSaveInterval, markdown, isMarkdownMode]);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 767px)");
    const updateViewport = () => setIsNarrow(media.matches);
    updateViewport();
    media.addEventListener("change", updateViewport);
    return () => media.removeEventListener("change", updateViewport);
  }, []);

  // Init EditorJS
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!ejInstance.current) {
        ejInstance.current = new EditorJS({
          holder: "editorjs-demo",
          autofocus: false,
          tools: {
            delimiter: Delimiter,
            header: {
              class: Header,
              inlineToolbar: ["link", "bold", "italic"],
              config: { levels: [2, 3, 4], defaultLevel: 2 },
            },
            paragraph: {
              class: Paragraph,
              inlineToolbar: ["link", "bold", "italic"],
            },
            list: List,
            quote: Quote,
          },
          onReady: () => console.log("✅ EditorJS Demo ready"),
        });
      }
    }, 100);

    return () => {
      clearTimeout(timeout);
      if (ejInstance.current && typeof ejInstance.current.destroy === "function") {
        ejInstance.current.destroy();
        ejInstance.current = null;
      }
    };
  }, []);

  // Toggle zwischen Markdown und EditorJS
  const toggleEditorMode = async () => {
    if (isMarkdownMode) {
      // Wechsel zu EditorJS: Markdown zu Blocks konvertieren
      const blocks = mdToEditorBlocks(markdown);
      if (ejInstance.current && typeof ejInstance.current.render === "function") {
        await ejInstance.current.render({ blocks });
      }
      setEditorData({ blocks });
    } else {
      // Wechsel zu Markdown: EditorJS Daten holen
      try {
        const output = await ejInstance.current.save();
        const md = editorBlocksToMarkdown(output.blocks);
        setMarkdown(md);
        setEditorData({ blocks: output.blocks });
      } catch (err) {
        console.error("Fehler beim Speichern:", err);
      }
    }
    setIsMarkdownMode(!isMarkdownMode);
  };

  // Speichern in LocalStorage (unterstützt beide Modi)
  const handleSave = async () => {
    try {
      let finalMarkdown = markdown;

      if (!isMarkdownMode) {
        // Im EditorJS-Modus: Daten zu Markdown konvertieren
        const output = await ejInstance.current.save();
        finalMarkdown = editorBlocksToMarkdown(output.blocks);
        setMarkdown(finalMarkdown);
        setEditorData({ blocks: output.blocks });
      }

      localStorage.setItem("zeneditor_demo_content", finalMarkdown);
      alert("✅ Inhalt gespeichert (LocalStorage)");
    } catch (err) {
      console.error("Fehler beim Speichern:", err);
      alert("Fehler beim Speichern. Schau in die Konsole.");
    }
  };

  // Reset zu Demo-Inhalt
  const handleReset = async () => {
    if (window.confirm("Demo-Inhalt wiederherstellen?")) {
      setMarkdown(DEMO_MARKDOWN);
      localStorage.setItem("zeneditor_demo_content", DEMO_MARKDOWN);

      // Wenn im EditorJS-Modus, auch den Editor zurücksetzen
      if (!isMarkdownMode) {
        const blocks = mdToEditorBlocks(DEMO_MARKDOWN);
        if (ejInstance.current && typeof ejInstance.current.render === "function") {
          await ejInstance.current.render({ blocks });
        }
      }
    }
  };

  // Download/Export - öffnet Export-Modal
  const handleDownload = async () => {
    try {
      let finalMarkdown = markdown;

      if (!isMarkdownMode) {
        // Im EditorJS-Modus: Daten zu Markdown konvertieren
        const output = await ejInstance.current.save();
        finalMarkdown = editorBlocksToMarkdown(output.blocks);
        setMarkdown(finalMarkdown); // Update markdown state for export modal
      }

      setShowExportModal(true);
    } catch (err) {
      console.error("Fehler beim Öffnen des Export-Modals:", err);
      alert("Fehler beim Export. Schau in die Konsole.");
    }
  };

  // Preview Toolbar Actions
  const handlePreviewAction = async (action) => {
    try {
      let finalMarkdown = markdown;

      if (!isMarkdownMode) {
        // Im EditorJS-Modus: Daten zu Markdown konvertieren
        const output = await ejInstance.current.save();
        finalMarkdown = editorBlocksToMarkdown(output.blocks);
        setMarkdown(finalMarkdown);
      }

      switch (action) {
        case "export":
          setShowExportModal(true);
          break;
        case "copy-html":
          // Convert markdown to simple HTML and copy to clipboard
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = finalMarkdown
            .replace(/^### (.*$)/gim, '<h3>$1</h3>')
            .replace(/^## (.*$)/gim, '<h2>$1</h2>')
            .replace(/^# (.*$)/gim, '<h1>$1</h1>')
            .replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>')
            .replace(/\_(.*?)\_/gim, '<em>$1</em>')
            .replace(/\[([^\]]+)\]\(([^\)]+)\)/gim, '<a href="$2">$1</a>')
            .replace(/\n\n/g, '</p><p>')
            .replace(/^\- (.+)$/gim, '<li>$1</li>');
          await navigator.clipboard.writeText(tempDiv.innerHTML);
          alert("✅ HTML in Zwischenablage kopiert!");
          break;
        case "copy-md":
          await navigator.clipboard.writeText(finalMarkdown);
          alert("✅ Markdown in Zwischenablage kopiert!");
          break;
        case "print":
          window.print();
          break;
        default:
          break;
      }
    } catch (err) {
      console.error("Fehler bei Preview-Aktion:", err);
      alert("Fehler bei der Aktion. Schau in die Konsole.");
    }
  };

  const applyMarkdownAction = (action) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const value = textarea.value;
    let start = textarea.selectionStart;
    let end = textarea.selectionEnd;
    const hasSelection = start !== end;

    const getLineRange = () => {
      const lineStart = value.lastIndexOf("\n", start - 1) + 1;
      let lineEnd = value.indexOf("\n", end);
      if (lineEnd === -1) lineEnd = value.length;
      return { lineStart, lineEnd };
    };

    const replaceRange = (rangeStart, rangeEnd, text) => {
      const nextValue = value.slice(0, rangeStart) + text + value.slice(rangeEnd);
      setMarkdown(nextValue);
      requestAnimationFrame(() => {
        textarea.focus();
        textarea.setSelectionRange(rangeStart, rangeStart + text.length);
      });
    };

    const wrapSelection = (before, after, placeholder = "text") => {
      const content = hasSelection ? value.slice(start, end) : placeholder;
      const nextValue =
        value.slice(0, start) + before + content + after + value.slice(end);
      setMarkdown(nextValue);
      const nextStart = start + before.length;
      const nextEnd = nextStart + content.length;
      requestAnimationFrame(() => {
        textarea.focus();
        textarea.setSelectionRange(nextStart, nextEnd);
      });
    };

    const prefixLines = (prefix) => {
      if (!hasSelection) {
        const { lineStart, lineEnd } = getLineRange();
        const line = value.slice(lineStart, lineEnd) || "";
        const nextLine = line
          .split("\n")
          .map((l) => (l.trim() ? `${prefix}${l}` : l))
          .join("\n");
        replaceRange(lineStart, lineEnd, nextLine);
        return;
      }

      const selected = value.slice(start, end);
      const nextSelected = selected
        .split("\n")
        .map((l) => (l.trim() ? `${prefix}${l}` : l))
        .join("\n");
      replaceRange(start, end, nextSelected);
    };

    switch (action) {
      case "bold":
        wrapSelection("**", "**");
        break;
      case "italic":
        wrapSelection("_", "_");
        break;
      case "strike":
        wrapSelection("~~", "~~");
        break;
      case "code":
        wrapSelection("`", "`", "code");
        break;
      case "codeblock":
        wrapSelection("```\n", "\n```", "code");
        break;
      case "h2":
        prefixLines("## ");
        break;
      case "h3":
        prefixLines("### ");
        break;
      case "quote":
        prefixLines("> ");
        break;
      case "ul":
        prefixLines("- ");
        break;
      case "ol":
        prefixLines("1. ");
        break;
      case "link": {
        const content = hasSelection ? value.slice(start, end) : "link text";
        const template = `[${content}](https://)`;
        const nextValue = value.slice(0, start) + template + value.slice(end);
        setMarkdown(nextValue);
        const nextStart = start + 1 + content.length + 2;
        const nextEnd = nextStart + "https://".length;
        requestAnimationFrame(() => {
          textarea.focus();
          textarea.setSelectionRange(nextStart, nextEnd);
        });
        break;
      }
      case "image": {
        const alt = hasSelection ? value.slice(start, end) : "alt text";
        const template = `![${alt}](https://)`;
        const nextValue = value.slice(0, start) + template + value.slice(end);
        setMarkdown(nextValue);
        const nextStart = start + 2 + alt.length + 2;
        const nextEnd = nextStart + "https://".length;
        requestAnimationFrame(() => {
          textarea.focus();
          textarea.setSelectionRange(nextStart, nextEnd);
        });
        break;
      }
      case "hr":
        replaceRange(start, end, "\n---\n");
        break;
      default:
        break;
    }
  };

  const getCaretCoordinates = (textarea, position) => {
    const style = window.getComputedStyle(textarea);
    const div = document.createElement("div");
    const span = document.createElement("span");

    div.style.position = "absolute";
    div.style.visibility = "hidden";
    div.style.whiteSpace = "pre-wrap";
    div.style.wordWrap = "break-word";
    div.style.overflow = "hidden";
    div.style.left = "0";
    div.style.top = "0";
    div.style.width = `${textarea.clientWidth}px`;

    const properties = [
      "fontFamily",
      "fontSize",
      "fontWeight",
      "fontStyle",
      "letterSpacing",
      "textTransform",
      "lineHeight",
      "textAlign",
      "paddingTop",
      "paddingRight",
      "paddingBottom",
      "paddingLeft",
      "borderTopWidth",
      "borderRightWidth",
      "borderBottomWidth",
      "borderLeftWidth",
      "boxSizing",
    ];

    properties.forEach((prop) => {
      div.style[prop] = style[prop];
    });

    div.textContent = textarea.value.substring(0, position);
    span.textContent = textarea.value.substring(position) || ".";
    div.appendChild(span);
    document.body.appendChild(div);

    const spanRect = span.getBoundingClientRect();
    const divRect = div.getBoundingClientRect();

    const top = spanRect.top - divRect.top - textarea.scrollTop;
    const left = spanRect.left - divRect.left - textarea.scrollLeft;

    document.body.removeChild(div);

    return { top, left };
  };

  const updateMiniToolbar = (nextSelection) => {
    const textarea = textareaRef.current;
    if (!textarea || !isMarkdownMode) {
      setMiniToolbar({ visible: false, top: 0, left: 0 });
      return;
    }

    const start = nextSelection?.start ?? textarea.selectionStart ?? 0;
    const end = nextSelection?.end ?? textarea.selectionEnd ?? 0;
    if (start === end) {
      setMiniToolbar({ visible: false, top: 0, left: 0 });
      return;
    }

    const coords = getCaretCoordinates(textarea, end);
    const offsetRight = 18;
    const boundedLeft = Math.min(
      Math.max(coords.left + offsetRight, 12),
      textarea.clientWidth - 12
    );

    setMiniToolbar({
      visible: true,
      top: Math.max(coords.top, 8),
      left: boundedLeft,
    });
  };

  const updateSelection = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const nextSelection = {
      start: textarea.selectionStart || 0,
      end: textarea.selectionEnd || 0,
    };
    setSelection(nextSelection);
    updateMiniToolbar(nextSelection);

    // Update selected text for AI Panel
    const selected = textarea.value.substring(nextSelection.start, nextSelection.end);
    setSelectedText(selected);
  };

  // AI Panel Handlers
  const handleInsertText = (text) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newText = markdown.slice(0, end) + "\n\n" + text + "\n\n" + markdown.slice(end);
    setMarkdown(newText);

    // Focus and set cursor after inserted text
    setTimeout(() => {
      textarea.focus();
      const newPos = end + text.length + 4;
      textarea.setSelectionRange(newPos, newPos);
    }, 0);
  };

  const handleReplaceText = (text) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newText = markdown.slice(0, start) + text + markdown.slice(end);
    setMarkdown(newText);

    // Focus and select replaced text
    setTimeout(() => {
      textarea.focus();
      const newEnd = start + text.length;
      textarea.setSelectionRange(start, newEnd);
    }, 0);
  };

  const getIsActive = (id) => {
    const value = markdown;
    if (!value) return false;
    const start = selection.start;
    const end = selection.end;
    const lineStart = value.lastIndexOf("\n", start - 1) + 1;
    let lineEnd = value.indexOf("\n", end);
    if (lineEnd === -1) lineEnd = value.length;
    const line = value.slice(lineStart, lineEnd);
    const offsetInLine = Math.max(0, start - lineStart);

    const isInCodeFence = (() => {
      const before = value.slice(0, start);
      const fences = before.match(/```/g);
      return fences ? fences.length % 2 === 1 : false;
    })();

    const hasWrapper = (delimiter) => {
      const before = value.lastIndexOf(delimiter, start - 1);
      const after = value.indexOf(delimiter, end);
      if (before === -1 || after === -1 || before === after) return false;
      if (before < lineStart || after > lineEnd) return false;
      return before < start && after >= end;
    };

    const hasSingleAsterisk = () => {
      const before = value.lastIndexOf("*", start - 1);
      const after = value.indexOf("*", end);
      if (before === -1 || after === -1 || before === after) return false;
      if (before < lineStart || after > lineEnd) return false;
      if (value[before - 1] === "*" || value[before + 1] === "*") return false;
      if (value[after - 1] === "*" || value[after + 1] === "*") return false;
      return before < start && after >= end;
    };

    const isInsideInlineCode = () => {
      const before = line.lastIndexOf("`", offsetInLine - 1);
      const after = line.indexOf("`", offsetInLine);
      if (before === -1 || after === -1 || before === after) return false;
      return before < offsetInLine && after > offsetInLine;
    };

    const isInsideLink = () => {
      const before = line.lastIndexOf("[", offsetInLine);
      const middle = line.indexOf("](", before);
      const after = line.indexOf(")", middle);
      if (before === -1 || middle === -1 || after === -1) return false;
      return offsetInLine > before && offsetInLine < after;
    };

    const isInsideImage = () => {
      const before = line.lastIndexOf("![", offsetInLine);
      const middle = line.indexOf("](", before);
      const after = line.indexOf(")", middle);
      if (before === -1 || middle === -1 || after === -1) return false;
      return offsetInLine > before && offsetInLine < after;
    };

    switch (id) {
      case "bold":
        return hasWrapper("**");
      case "italic":
        return hasWrapper("_") || hasSingleAsterisk();
      case "strike":
        return hasWrapper("~~");
      case "codeblock":
        return isInCodeFence;
      case "code":
        return !isInCodeFence && isInsideInlineCode();
      case "h2":
        return line.startsWith("## ");
      case "h3":
        return line.startsWith("### ");
      case "quote":
        return line.startsWith("> ");
      case "ul":
        return /^(-|\*)\s+/.test(line);
      case "ol":
        return /^\d+\.\s+/.test(line);
      case "link":
        return isInsideLink();
      case "image":
        return isInsideImage();
      default:
        return false;
    }
  };

  // Determine if dark theme should be used
  const isDarkTheme = settings.theme === 'dark' ||
    (settings.theme === 'auto' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  const renderKiNoticePanel = () => (
    <PaperBG className="max-w-full mx-auto rounded-xl p-8">
      <div className="mb-4">
        <h2 className="text-xl font-bold text-brand">Sandbox Hinweis</h2>
        <p className="text-xs text-gray-600 mt-1">
          KI ist lokal (Ollama) und in der Demo nicht verfugbar.
        </p>
      </div>
      <div className="border-t border-brand pt-6">
        <p className="text-sm text-slate-800">
          Diese Demo ist eine Sandbox. Die KI lauft bei dir lokal uber Ollama,
          deshalb kannst du hier keine KI nutzen.
        </p>
        <p className="text-sm text-slate-800 mt-3">
          Wenn du es mit KI nutzen willst, frag mich an. Ich stelle dir ZenPost Studio
          mit KI Ollama als Demo zur Verfugung.
        </p>
        <div className="mt-6 border-t border-gray-200 pt-6">
          <p className="text-xs text-gray-600 mb-4">Demo ZenPost Studio mit KI anfragen</p>
          <CustomCourseRequestPr
            autoOpen
            initialStep="form"
            initialCategory="Eigene Anfrage"
            initialProgram="ich habe eine eigene Anfrage jetzt"
          />
        </div>
      </div>
    </PaperBG>
  );

  return (
    <div className={`max-w-7xl mx-auto px-4 py-8 font-mono ${isDarkTheme ? 'dark' : ''}`}>
      {/* Back Button */}
     

      {/* Header */}
      <PaperDarkBG className="rounded-lg mb-6">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <FaCode className="text-brand text-xl" />
              <div>
                <h1 className="text-sm font-semibold text-shadow">ZenEditor Demo</h1>
                <p className="text-xs text-gray-300">Hybrid Markdown/EditorJS Editor</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <MonoButton
                onClick={() => {
                  setShowAIPanel(!showAIPanel);
                  if (!showAIPanel) setShowCodePanel(false);
                }}
                className={`text-xs px-3 py-1 ${showAIPanel ? 'bg-brand text-slate-50' : ''}`}
              >
                <span className="flex items-center gap-2">
                  <FaMagic /> Text-AI {showAIPanel ? '✓' : ''}
                </span>
              </MonoButton>
              <MonoButton
                onClick={() => {
                  setShowCodePanel(!showCodePanel);
                  if (!showCodePanel) setShowAIPanel(false);
                }}
                className={`text-xs px-3 py-1 ${showCodePanel ? 'bg-purple-600 text-slate-50' : ''}`}
              >
                <span className="flex items-center gap-2">
                  <FaTerminal /> Code-AI {showCodePanel ? '✓' : ''}
                </span>
              </MonoButton>
              <MonoButton onClick={handleSave} className="text-xs px-3 py-1">
                <span className="flex items-center gap-2">
                  <FaSave /> Speichern
                </span>
              </MonoButton>
              <MonoButton onClick={handleReset} className="text-xs px-3 py-1">
                <span className="flex items-center gap-2">
                  <FaTrash /> Reset
                </span>
              </MonoButton>
              <MonoButton
                onClick={() => setShowSettings(true)}
                className="text-xs px-3 py-1"
              >
                <span className="flex items-center gap-2">
                  <FaCog /> Einstellungen
                </span>
              </MonoButton>
            </div>
          </div>
        </div>
      </PaperDarkBG>

      {/* Info Banner */}
      <div className="bg-gradient-to-r from-orange-50 to-orange-100 border-l-4 border-brand px-4 py-3 mb-6 rounded">
        <p className="text-xs text-gray-800">
          <strong>Demo-Modus:</strong> Dies ist eine Sandbox-Version. Alle Änderungen werden nur in deinem Browser gespeichert (LocalStorage).
          Für den vollen ZenEditor mit Backend-Integration und EditorJS-Support{" "}
          <button
            onClick={() => setShowBooking(true)}
            className="text-brand underline hover:no-underline font-semibold cursor-pointer"
          >
            Sag Hallo 
          </button>.
        </p>
      </div>

      {/* AI Panels */}
      {showAIPanel && (
        <div className="mb-6">
          <ZenEditorAIPanel
            selectedText={selectedText}
            onInsertText={handleInsertText}
            onReplaceText={handleReplaceText}
          />
        </div>
      )}

      {showCodePanel && (
        <div className="mb-6">
          <ZenEditorCodePanel
            selectedText={selectedText}
            onInsertText={handleInsertText}
            onReplaceText={handleReplaceText}
          />
        </div>
      )}

      {/* Toggle Buttons */}
      <div className="mb-4 flex gap-2 flex-wrap">
        <MonoButton
          onClick={toggleEditorMode}
          className="text-xs px-3 py-1 flex items-center gap-2 bg-[#171717] hover:bg-[transparent]"
        >

          {isMarkdownMode ? "Zu EditorJS wechseln" : "Zu Markdown wechseln"}
        </MonoButton>
        <MonoButton
          onClick={() => setShowPreview(!showPreview)}
          className="text-xs px-3 py-1"
        >

          {showPreview ? "Zu Nur Editor wechseln" : "Zu Editor & Preview wechseln"}
        </MonoButton>
      </div>

      {/* Workflow */}
      <div className="mb-4 rounded-lg border border-brand bg-[#171717] px-4 py-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-brand/70">Workflow</p>
            <h2 className="text-sm font-semibold text-brand">
              Step {WORKFLOW_STEPS[activeWorkflowStep].step} · {WORKFLOW_STEPS[activeWorkflowStep].title}
            </h2>
            <p className="text-xs text-[#EFEBDC]">
              {WORKFLOW_STEPS[activeWorkflowStep].description}
            </p>
          </div>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4 text-xs">
          {WORKFLOW_STEPS.map((stepItem, index) => {
            const isActive = index === activeWorkflowStep;
            return (
              <button
                key={stepItem.step}
                type="button"
                onClick={() => setActiveWorkflowStep(index)}
                className={`rounded-md border p-3 text-left transition ${
                  isActive
                    ? "border-brand bg-slate-800/80"
                    : "border-white/10 bg-white/5 hover:border-brand/60"
                }`}
              >
                <div className={`text-[10px] font-semibold ${isActive ? "text-brand" : "text-gray-300"}`}>
                  Step {stepItem.step}
                </div>
                <div className={`mt-1 font-semibold ${isActive ? "text-[#EFEBDC]" : "text-gray-200"}`}>
                  {stepItem.title}
                </div>
                <p className={`mt-1 ${isActive ? "text-[#EFEBDC]/80" : "text-gray-400"}`}>
                  {stepItem.description}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Editor + Preview Grid / Export */}
      {activeWorkflowStep === 1 ? (
        <div className="mb-6">
          <ZenEditorExportModal
            inline
            markdown={markdown}
            onClose={() => setActiveWorkflowStep(0)}
          />
        </div>
      ) : activeWorkflowStep === 2 ? (
        <div className="mb-6">{renderKiNoticePanel()}</div>
      ) : (
        <div className={`grid ${showPreview ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1"} gap-6`}>
          {/* Editor */}
          <div>
            <PaperDarkBG className="rounded-lg mb-2">
              <div className="px-4 py-2">
                <h3 className="text-xs font-semibold text-shadow flex items-center gap-2">
                  <FaCode className="text-brand" />
                  {isMarkdownMode ? "Markdown Editor" : "EditorJS"}
                </h3>
              </div>
            </PaperDarkBG>

            {/* Markdown Textarea */}
            {isMarkdownMode && (
              <MarkdownToolbar
                onAction={applyMarkdownAction}
                size={isNarrow ? "sm" : "md"}
                variant="glass"
                layout="inline"
                getIsActive={getIsActive}
                showLabels={false}
                wrap={!isNarrow}
                className={isNarrow ? "overflow-x-auto" : ""}
              />
            )}
            <div className={`relative ${isMarkdownMode ? "" : "hidden"}`}>
              {miniToolbar.visible && (
                <div
                  className="pointer-events-none absolute z-10 -translate-y-full"
                  style={{ top: miniToolbar.top, left: miniToolbar.left }}
                >

                      {/* Markdown  miniToolbar Textarea */}
                  <div className="pointer-events-auto">
                    <MarkdownToolbar
                      onAction={applyMarkdownAction}
                      tools={["bold", "italic", "strike", "code", "link", "image"]}
                      size={isNarrow ? "xs" : "sm"}
                      variant="solid"
                      layout="inline"
                      showLabels={false}
                      wrap={false}
                      getIsActive={getIsActive}
                      withMargin={false}
                    />
                  </div>
                </div>
              )}
              <textarea
                ref={textareaRef}
                value={markdown}
                onChange={(e) => setMarkdown(e.target.value)}
                onSelect={updateSelection}
                onKeyUp={updateSelection}
                onMouseUp={updateSelection}
                onFocus={updateSelection}
                onBlur={() => setMiniToolbar({ visible: false, top: 0, left: 0 })}
                onScroll={updateSelection}
                style={{
                  fontSize: `${settings.fontSize}px`,
                  whiteSpace: settings.wordWrap ? 'pre-wrap' : 'pre',
                  wordWrap: settings.wordWrap ? 'break-word' : 'normal',
                  backgroundColor: isDarkTheme ? '#1a1a1a' : '#ffffff',
                  color: isDarkTheme ? '#e0e0e0' : '#000000',
                }}
                spellCheck={settings.spellCheck}
                className={`w-full h-[600px] p-4 font-mono border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand ${
                  isDarkTheme ? 'border-gray-700' : 'border-gray-300'
                }`}
                placeholder="Schreibe deinen Markdown-Content hier..."
              />
            </div>

            {/* EditorJS Container */}
            <div
              id="editorjs-demo"
              style={{
                backgroundColor: isDarkTheme ? '#1a1a1a' : '#ffffff',
                color: isDarkTheme ? '#e0e0e0' : '#000000',
              }}
              className={`w-full min-h-[600px] p-4 border rounded-lg ${
                isDarkTheme ? 'border-gray-700' : 'border-gray-300'
              } ${isMarkdownMode ? "hidden" : ""}`}
            ></div>
          </div>

          {/* Preview */}
          {showPreview && (
            <div>
              <PaperDarkBG className="rounded-lg mb-2">
                <div className="px-4 py-2">
                  <h3 className="text-xs font-semibold text-shadow flex items-center gap-2">
                    <FaCode className="text-brand" />
                    Live Preview
                  </h3>
                </div>
              </PaperDarkBG>

              {/* Preview Toolbar */}
              <PreviewToolbar
                onAction={handlePreviewAction}
                size={isNarrow ? "sm" : "md"}
                variant="glass"
                layout="inline"
                showLabels={false}
                wrap={!isNarrow}
                className={isNarrow ? "overflow-x-auto" : ""}
              />

              <div
                className={`w-full h-[600px] p-4 border rounded-lg overflow-y-auto ${
                  isDarkTheme ? ' bg-[#151515] border-gray-700' : 'bg-[#D9D4C5] border-brand'
                }`}
              >
                <div className="prose prose-sm max-w-none">
                  <ReactMarkdown
                    components={{
                      h1: ({node, ...props}) => <h1 className={`text-2xl font-bold mb-4 ${isDarkTheme ? 'text-gray-100' : 'text-gray-900'}`} {...props} />,
                      h2: ({node, ...props}) => <h2 className={`text-xl font-bold mb-3 ${isDarkTheme ? 'text-gray-100' : 'text-gray-900'}`} {...props} />,
                      h3: ({node, ...props}) => <h3 className={`text-lg font-bold mb-2 ${isDarkTheme ? 'text-gray-100' : 'text-gray-900'}`} {...props} />,
                      p: ({node, ...props}) => <p className={`mb-4 leading-relaxed ${isDarkTheme ? 'text-gray-300' : 'text-gray-800'}`} {...props} />,
                      ul: ({node, ...props}) => <ul className={`list-disc ml-6 mb-4 ${isDarkTheme ? 'text-gray-300' : 'text-gray-800'}`} {...props} />,
                      ol: ({node, ...props}) => <ol className={`list-decimal ml-6 mb-4 ${isDarkTheme ? 'text-gray-300' : 'text-gray-800'}`} {...props} />,
                      li: ({node, ...props}) => <li className="mb-1" {...props} />,
                      blockquote: ({node, ...props}) => (
                        <blockquote className={`border-l-4 border-brand pl-4 italic my-4 ${isDarkTheme ? 'text-gray-400' : 'text-gray-700'}`} {...props} />
                      ),
                      code: ({node, inline, className, children, ...props}) => {
                        return inline ? (
                          <code className="bg-gray-100 px-1 rounded text-xs text-brand font-mono" {...props}>
                            {children}
                          </code>
                        ) : (
                          <code className="block  bg-[#171717] text-green-400 p-4 rounded my-4 overflow-x-auto text-xs font-mono" {...props}>
                            {children}
                          </code>
                        );
                      },
                      hr: ({node, ...props}) => <hr className="my-8 border-t-2 border-gray-300" {...props} />,
                    }}
                  >
                    {markdown}
                  </ReactMarkdown>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Footer CTA */}




 <ZenDemoFooter />

      {/* Booking Modal */}
      {showBooking && <BookingModal onClose={() => setShowBooking(false)} />}

      {/* Settings Modal */}
      {showSettings && <ZenEditorSettings onClose={() => setShowSettings(false)} />}

      {/* Export Modal */}
      {showExportModal && (
        <ZenEditorExportModal
          markdown={markdown}
          onClose={() => setShowExportModal(false)}
        />
      )}
    </div>
  );
}

export default ZenEditorDemo;
